import React, { useCallback, useMemo, useState } from 'react';
import HotelIcon from '@mui/icons-material/Hotel';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import PriceChangeIcon from '@mui/icons-material/PriceChange';
import SearchIcon from '@mui/icons-material/Search';
import SortIcon from '@mui/icons-material/Sort';
import StarIcon from '@mui/icons-material/Star';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import { useTranslation } from 'react-i18next';
import { useListingColors } from 'legacyfrontend/hooks/useListingColors';
import type { ReactNode } from 'react';
import type { HotelFilterState } from '../types/hotels.presentation';
import type { MealOption, HotelTypeOption } from '../constants/hotelMasterData';

interface HotelFiltersProps {
  filters: HotelFilterState;
  onFiltersChange: (filters: HotelFilterState) => void;
  priceBounds?: [number, number];
  /** Static meal filter options (BM_MEALS). */
  mealOptions?: MealOption[];
  /** Static hotel type filter options (BM_HOTEL_TYPES). */
  hotelTypeOptions?: HotelTypeOption[];
  /** Facet counts: BM meal code → number of matching hotels. */
  mealCounts?: Record<string, number>;
  /** Facet counts: BM hotel type id → number of matching hotels. */
  hotelTypeCounts?: Record<number, number>;
  /** When explicitly false, hides the entire promotions section. */
  hasPromotionsAvailable?: boolean;
}

const STAR_OPTIONS = [3, 4, 5] as const;
const DEFAULT_PRICE_BOUNDS: [number, number] = [0, 1000];

const AccordionSection = ({
  children,
  icon,
  isOpen,
  onToggle,
  title,
}: {
  children: ReactNode;
  icon: ReactNode;
  isOpen: boolean;
  onToggle: () => void;
  title: string;
}) => (
  <div>
    <button
      aria-expanded={isOpen}
      className='mb-2 flex w-full items-center justify-between rounded-md py-2 text-left text-sm font-medium uppercase text-black hover:translate-y-0 active:translate-y-0'
      onClick={onToggle}
      style={{ transform: 'none' }}
      type='button'
    >
      <span className='flex items-center gap-2'>
        {icon}
        <span>{title}</span>
      </span>
      <span aria-hidden='true' className='flex h-5 w-5 items-center justify-center leading-none'>
        {isOpen ? (
          <KeyboardArrowUpIcon fontSize='small' />
        ) : (
          <KeyboardArrowDownIcon fontSize='small' />
        )}
      </span>
    </button>
    <div className={isOpen ? 'flex flex-col gap-4' : 'hidden'}>{children}</div>
  </div>
);

export function HotelFilters({
  filters,
  onFiltersChange,
  priceBounds = DEFAULT_PRICE_BOUNDS,
  mealOptions,
  hotelTypeOptions,
  mealCounts,
  hotelTypeCounts,
  hasPromotionsAvailable,
}: HotelFiltersProps) {
  const { t, i18n } = useTranslation();
  const { background } = useListingColors();
  const [local, setLocal] = useState<HotelFilterState>(filters);
  const [dropdownState, setDropdownState] = useState({
    name: true,
    price: true,
    meals: true,
    stars: true,
    hotelType: true,
  });
  const [hoveredStar, setHoveredStar] = useState<number | undefined>();
  const selectedStars = useMemo(() => local.starRatings ?? [], [local.starRatings]);
  const selectedMeals = useMemo(() => local.selectedMeals ?? [], [local.selectedMeals]);
  const selectedHotelTypes = useMemo(
    () => local.selectedHotelTypes ?? [],
    [local.selectedHotelTypes],
  );

  const toggleDropdown = useCallback((filter: keyof typeof dropdownState) => {
    setDropdownState((current) => ({ ...current, [filter]: !current[filter] }));
  }, []);

  const update = useCallback(
    (patch: Partial<HotelFilterState>) => {
      const next = { ...local, ...patch };
      setLocal(next);
      onFiltersChange(next);
    },
    [local, onFiltersChange],
  );

  const minBound = priceBounds[0];
  const maxBound = Math.max(priceBounds[1], minBound + 1);
  const selectedMin = local.priceMin ?? minBound;
  const selectedMax = local.priceMax ?? maxBound;

  const handleAllStars = useCallback(() => {
    update({ starRatings: [] });
  }, [update]);

  const toggleStar = useCallback(
    (star: number) => {
      const nextStars = selectedStars.includes(star)
        ? selectedStars.filter((selected) => selected !== star)
        : [...selectedStars, star].sort((a, b) => a - b);

      update({ starRatings: nextStars });
    },
    [selectedStars, update],
  );

  const isStarSelected = useCallback(
    (star: number) => selectedStars.includes(star),
    [selectedStars],
  );

  const updatePriceMin = useCallback(
    (value: number | undefined) => {
      if (value === undefined) {
        update({ priceMin: undefined });
        return;
      }

      const nextMin = Math.max(minBound, Math.min(value, selectedMax));
      update({ priceMin: nextMin === minBound ? undefined : nextMin });
    },
    [minBound, selectedMax, update],
  );

  const updatePriceMax = useCallback(
    (value: number | undefined) => {
      if (value === undefined) {
        update({ priceMax: undefined });
        return;
      }

      const nextMax = Math.min(maxBound, Math.max(value, selectedMin));
      update({ priceMax: nextMax === maxBound ? undefined : nextMax });
    },
    [maxBound, selectedMin, update],
  );

  const clearAll = useCallback(() => {
    const empty: HotelFilterState = {};
    setLocal(empty);
    onFiltersChange(empty);
  }, [onFiltersChange]);

  const toggleMeal = useCallback(
    (code: string) => {
      const next = selectedMeals.includes(code)
        ? selectedMeals.filter((v) => v !== code)
        : [...selectedMeals, code];
      update({ selectedMeals: next.length > 0 ? next : undefined });
    },
    [selectedMeals, update],
  );

  const toggleHotelType = useCallback(
    (typeId: number) => {
      const next = selectedHotelTypes.includes(typeId)
        ? selectedHotelTypes.filter((v) => v !== typeId)
        : [...selectedHotelTypes, typeId];
      update({ selectedHotelTypes: next.length > 0 ? next : undefined });
    },
    [selectedHotelTypes, update],
  );

  const sortLabels = useMemo(() => {
    const lang = (i18n as { language?: string }).language ?? 'es-AR';
    if (lang.startsWith('en'))
      return { label: 'Sort', asc: 'Lowest price', desc: 'Highest price', none: 'Unsorted' };
    if (lang.startsWith('pt'))
      return { label: 'Ordenar', asc: 'Menor preço', desc: 'Maior preço', none: 'Sem ordenar' };
    return { label: 'Ordenar', asc: 'Menor precio', desc: 'Mayor precio', none: 'Sin ordenar' };
  }, [i18n]);

  const handleSortChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const value = e.target.value;
      update({
        sortBy: value === 'price-asc' || value === 'price-desc' ? value : undefined,
      });
    },
    [update],
  );

  return (
    <section
      aria-label={t('hotels.filters.filterLabel')}
      className='relative flex w-full overflow-hidden rounded-xl bg-gray-100 lg:min-h-screen lg:w-[300px] lg:shrink-0'
    >
      <div className='flex h-max w-full flex-1 flex-col gap-4 rounded-xl bg-white px-4 py-4 lg:max-w-[300px]'>
        <div className='aside-flights-filters__header flex items-center justify-between gap-4'>
          <h2 className='text-2xl font-medium uppercase text-gray-900'>FILTROS</h2>
          <button
            aria-label={t('hotels.filters.clear')}
            className='aside-flights-filters__clean-btn rounded-md px-2 py-1 text-sm text-gray-700 transition-colors hover:bg-gray-50'
            onClick={clearAll}
            type='button'
          >
            Limpiar
          </button>
        </div>

        <div className='flex items-center gap-2'>
          <SortIcon className='h-5 w-5' sx={{ color: background }} />
          <label className='text-sm font-medium uppercase text-black'>{sortLabels.label}</label>
          <select
            aria-label={sortLabels.label}
            className='ml-auto rounded-md border border-gray-300 py-1 pl-2 pr-8 text-sm focus:outline-none focus:ring-2'
            onChange={handleSortChange}
            value={local.sortBy ?? ''}
          >
            <option value=''>{sortLabels.none}</option>
            <option value='price-asc'>{sortLabels.asc}</option>
            <option value='price-desc'>{sortLabels.desc}</option>
          </select>
        </div>

        <AccordionSection
          icon={<SearchIcon className='h-5 w-5' sx={{ color: background }} />}
          isOpen={dropdownState.name}
          onToggle={() => toggleDropdown('name')}
          title={t('hotels.filters.name')}
        >
          <div className='filter-section flex flex-col gap-3'>
            <input
              aria-label={t('hotels.filters.name')}
              className='w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2'
              id='hotel-filter-name'
              onChange={(e) => update({ name: e.target.value || undefined })}
              placeholder={t('hotels.filters.namePlaceholder')}
              type='text'
              value={local.name ?? ''}
            />
          </div>
        </AccordionSection>

        <div className='h-px bg-gray-200' />

        {/* Meals section: always visible when mealOptions are provided (static BM_MEALS). */}
        {mealOptions && mealOptions.length > 0 ? (
          <>
            <AccordionSection
              icon={<RestaurantMenuIcon className='h-5 w-5' sx={{ color: background }} />}
              isOpen={dropdownState.meals}
              onToggle={() => toggleDropdown('meals')}
              title={t('hotels.filters.meals')}
            >
              <div className='filter-section flex flex-col gap-3'>
                <div
                  aria-label={t('hotels.filters.meals')}
                  className='flex flex-wrap gap-2'
                  role='group'
                >
                  {mealOptions.map((meal) => {
                    const isSelected = selectedMeals.includes(meal.code);
                    const count = mealCounts ? (mealCounts[meal.code] ?? 0) : undefined;
                    const isZero = count !== undefined && count === 0;
                    if (isZero && !isSelected) return null;
                    return (
                      <button
                        aria-pressed={isSelected}
                        className='inline-flex min-h-[34px] items-center rounded-lg border px-3 py-2 text-xs font-medium leading-tight shadow-sm transition-all duration-200 ease-out hover:-translate-y-0.5 hover:bg-gray-50 hover:shadow-md active:translate-y-0 active:scale-[0.98] motion-reduce:transform-none motion-reduce:transition-none'
                        key={`meal-${meal.code}`}
                        onClick={() => toggleMeal(meal.code)}
                        style={{
                          backgroundColor: isSelected ? '#F0F7FF' : '#FFFFFF',
                          borderColor: isSelected ? background : '#E5E7EB',
                          boxShadow: isSelected ? '0 4px 10px rgba(0, 68, 204, 0.12)' : undefined,
                          color: isSelected ? background : '#374151',
                        }}
                        type='button'
                      >
                        {meal.label}
                        {count !== undefined && (
                          <span
                            aria-hidden='true'
                            className='ml-1.5 inline-flex min-w-[18px] items-center justify-center rounded-full bg-gray-200 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-gray-600'
                          >
                            {count}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </AccordionSection>
            <div className='h-px bg-gray-200' />
          </>
        ) : null}

        {/* Hotel type section: always visible when hotelTypeOptions are provided (static BM_HOTEL_TYPES). */}
        {hotelTypeOptions && hotelTypeOptions.length > 0 ? (
          <>
            <AccordionSection
              icon={<HotelIcon className='h-5 w-5' sx={{ color: background }} />}
              isOpen={dropdownState.hotelType}
              onToggle={() => toggleDropdown('hotelType')}
              title={t('hotels.filters.hotelType')}
            >
              <div className='filter-section flex flex-col gap-3'>
                <div
                  aria-label={t('hotels.filters.hotelType')}
                  className='flex flex-wrap gap-2'
                  role='group'
                >
                  {hotelTypeOptions.map((type) => {
                    const isSelected = selectedHotelTypes.includes(type.id);
                    const count = hotelTypeCounts ? (hotelTypeCounts[type.id] ?? 0) : undefined;
                    const isZero = count !== undefined && count === 0;
                    if (isZero && !isSelected) return null;
                    return (
                      <button
                        aria-pressed={isSelected}
                        className='inline-flex min-h-[34px] items-center rounded-lg border px-3 py-2 text-xs font-medium leading-tight shadow-sm transition-all duration-200 ease-out hover:-translate-y-0.5 hover:bg-gray-50 hover:shadow-md active:translate-y-0 active:scale-[0.98] motion-reduce:transform-none motion-reduce:transition-none'
                        key={`hotel-type-${type.id}`}
                        onClick={() => toggleHotelType(type.id)}
                        style={{
                          backgroundColor: isSelected ? '#F0F7FF' : '#FFFFFF',
                          borderColor: isSelected ? background : '#E5E7EB',
                          boxShadow: isSelected ? '0 4px 10px rgba(0, 68, 204, 0.12)' : undefined,
                          color: isSelected ? background : '#374151',
                        }}
                        type='button'
                      >
                        {type.label}
                        {count !== undefined && (
                          <span
                            aria-hidden='true'
                            className='ml-1.5 inline-flex min-w-[18px] items-center justify-center rounded-full bg-gray-200 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-gray-600'
                          >
                            {count}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </AccordionSection>
            <div className='h-px bg-gray-200' />
          </>
        ) : null}

        {/* Promotions switch: hidden entirely when hasPromotionsAvailable is explicitly false. */}
        {hasPromotionsAvailable !== false ? (
          <>
            <div className='flex items-center justify-between py-2'>
              <span className='flex items-center gap-2'>
                <LocalOfferIcon className='h-5 w-5' sx={{ color: background }} />
                <span className='text-sm font-medium uppercase text-black'>
                  {t('hotels.filters.promotions')}
                </span>
              </span>
              <button
                aria-checked={local.promotionsOnly === true}
                aria-label={t('hotels.filters.promotionsOnly')}
                className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                  local.promotionsOnly ? '' : 'bg-gray-200'
                }`}
                onClick={() =>
                  update({
                    promotionsOnly: local.promotionsOnly ? undefined : true,
                  })
                }
                role='switch'
                style={local.promotionsOnly ? { backgroundColor: background } : undefined}
                type='button'
              >
                <span
                  aria-hidden='true'
                  className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform duration-200 ${
                    local.promotionsOnly ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
            <div className='h-px bg-gray-200' />
          </>
        ) : null}

        <AccordionSection
          icon={<PriceChangeIcon className='h-5 w-5' sx={{ color: background }} />}
          isOpen={dropdownState.price}
          onToggle={() => toggleDropdown('price')}
          title={t('hotels.filters.priceRange')}
        >
          <div className='filter-section flex items-center flex-col'>
            <div className='relative my-2 h-8 w-[90%]'>
              <div className='absolute left-0 right-0 top-1/2 h-1 -translate-y-1/2 rounded-full bg-gray-200' />
              <div
                aria-hidden='true'
                className='absolute top-1/2 h-1 -translate-y-1/2 rounded-full'
                style={{
                  backgroundColor: background,
                  left: `${((selectedMin - minBound) / (maxBound - minBound)) * 100}%`,
                  right: `${100 - ((selectedMax - minBound) / (maxBound - minBound)) * 100}%`,
                }}
              />
              <input
                aria-label={`${t('hotels.filters.priceMin')} slider`}
                className='pointer-events-none absolute left-0 top-0 h-8 w-full appearance-none bg-transparent accent-current [&::-webkit-slider-thumb]:pointer-events-auto'
                max={maxBound}
                min={minBound}
                onChange={(e) => updatePriceMin(Number(e.target.value))}
                style={{ color: background }}
                type='range'
                value={selectedMin}
              />
              <input
                aria-label={`${t('hotels.filters.priceMax')} slider`}
                className='pointer-events-none absolute left-0 top-0 h-8 w-full appearance-none bg-transparent accent-current [&::-webkit-slider-thumb]:pointer-events-auto'
                max={maxBound}
                min={minBound}
                onChange={(e) => updatePriceMax(Number(e.target.value))}
                style={{ color: background }}
                type='range'
                value={selectedMax}
              />
            </div>
            <div className='mt-4 flex gap-4'>
              <input
                aria-label={t('hotels.filters.priceMin')}
                className='flex-1 w-full rounded-md border border-gray-300 px-3 py-2 text-right text-sm focus:outline-none focus:ring-2'
                min={minBound}
                onChange={(e) => {
                  const v = e.target.value ? Number(e.target.value) : undefined;
                  updatePriceMin(v);
                }}
                placeholder={t('hotels.filters.priceMin')}
                type='number'
                value={selectedMin}
              />
              <input
                aria-label={t('hotels.filters.priceMax')}
                className='flex-1 w-full rounded-md border border-gray-300 px-3 py-2 text-right text-sm focus:outline-none focus:ring-2'
                min={minBound}
                onChange={(e) => {
                  const v = e.target.value ? Number(e.target.value) : undefined;
                  updatePriceMax(v);
                }}
                placeholder={t('hotels.filters.priceMax')}
                type='number'
                value={selectedMax}
              />
            </div>
          </div>
        </AccordionSection>

        <div className='h-px bg-gray-200' />

        <AccordionSection
          icon={<StarIcon className='h-5 w-5' sx={{ color: background }} />}
          isOpen={dropdownState.stars}
          onToggle={() => toggleDropdown('stars')}
          title={t('hotels.filters.stars')}
        >
          <div className='filter-section flex flex-col gap-3'>
            <div
              aria-label={t('hotels.filters.stars')}
              className='grid w-full grid-cols-2 gap-2'
              onMouseLeave={() => setHoveredStar(undefined)}
              role='group'
            >
              <button
                aria-label={t('hotels.filters.allStarsAriaLabel')}
                aria-pressed={selectedStars.length === 0}
                className='flex min-h-[44px] items-center justify-center rounded-lg border px-3 py-2 text-sm font-medium leading-none shadow-sm transition-all duration-200 ease-out hover:-translate-y-0.5 hover:shadow-md active:translate-y-0 active:scale-[0.98] motion-reduce:transform-none motion-reduce:transition-none'
                onClick={handleAllStars}
                style={{
                  backgroundColor: selectedStars.length === 0 ? background : '#FFFFFF',
                  borderColor: selectedStars.length === 0 ? background : '#E5E7EB',
                  color: selectedStars.length === 0 ? '#FFFFFF' : '#374151',
                }}
                type='button'
              >
                {t('hotels.filters.allStars')}
              </button>
              {STAR_OPTIONS.map((star) => (
                <button
                  aria-label={`${star} ${t('hotels.filters.starAriaLabel')}`}
                  aria-pressed={isStarSelected(star)}
                  className='group flex min-h-[44px] items-center justify-center gap-1 rounded-lg border px-2 py-2 text-sm font-medium leading-none shadow-sm transition-all duration-200 ease-out hover:-translate-y-0.5 hover:shadow-md active:translate-y-0 active:scale-[0.98] motion-reduce:transform-none motion-reduce:transition-none'
                  data-filled={isStarSelected(star) || hoveredStar === star}
                  key={star}
                  onBlur={() => setHoveredStar(undefined)}
                  onClick={() => toggleStar(star)}
                  onFocus={() => setHoveredStar(star)}
                  onMouseEnter={() => setHoveredStar(star)}
                  onMouseLeave={() => setHoveredStar(undefined)}
                  style={{
                    backgroundColor: isStarSelected(star) ? '#FFFBEB' : '#FFFFFF',
                    borderColor: isStarSelected(star) ? '#F59E0B' : '#E5E7EB',
                    color: isStarSelected(star) ? '#92400E' : '#374151',
                    boxShadow: isStarSelected(star)
                      ? '0 4px 10px rgba(245, 158, 11, 0.18)'
                      : undefined,
                  }}
                  type='button'
                >
                  <span aria-hidden='true' className='flex items-center gap-0.5'>
                    {Array.from({ length: star }).map((_, index) => (
                      <StarIcon
                        className='h-4 w-4 transition-all duration-200 ease-out group-hover:scale-110 motion-reduce:transform-none motion-reduce:transition-none'
                        key={`${star}-${index}`}
                        style={{ transitionDelay: `${index * 20}ms` }}
                        sx={{
                          color:
                            isStarSelected(star) || hoveredStar === star ? '#F59E0B' : '#D1D5DB',
                        }}
                      />
                    ))}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </AccordionSection>
      </div>
    </section>
  );
}
